import { Navigate, Outlet, useLocation } from "@tanstack/react-router";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { useAuth } from "@/providers/useAuth";
import { isAccessDeniedEntry, isReturningUser } from "@/shared/auth/returningUser";
import { apiConfig } from "@/shared/config/apiConfig";
import AppHeader from "@/shared/ui/appHeader/AppHeader";
import { footerPrimaryButtonSx } from "@/shared/ui/dialogFooterButtons";
import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from "@/shared/ui/appHeader/mobileBottomNav";
import QuotaBanner from "@/shared/ui/QuotaBanner";
import DemoModeBanner from "@/shared/ui/DemoModeBanner";
import RouteFallback from "@/shared/ui/RouteFallback";
import AgentDrawerLauncher from "@/domains/agent/ui/AgentDrawerLauncher";
import { AgentSessionProvider } from "@/domains/agent/ui/AgentSessionProvider";
import { agentConfig } from "@/shared/config/agentConfig";
import { readAgentDrawerWidth, writeAgentDrawerWidth } from "@/domains/agent/ui/agentDrawerLayout";
import { DockedAsideProvider } from "@/shared/ui/dockedAsideContext";
import { chromeRemaining } from "@/shared/ui/compactChrome/chromeCss";
import { compactChromeActive, useCompactChrome } from "@/shared/ui/compactChrome/useCompactChrome";
import {
  shellHeightSx,
  useVisualViewportHeight,
} from "@/shared/ui/compactChrome/useVisualViewportHeight";
import { useCompactLayout, useHeaderCompact } from "@/shared/ui/useCompactLayout";

function shellMarginTransition(theme: Theme, agentDocked: boolean): string {
  if (agentDocked) return "none";
  return theme.transitions.create("margin-right", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  });
}

export function RootLayout() {
  const { isAuthenticated, isAuthResolved } = useAuth();
  const location = useLocation();
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentWidth, setAgentWidth] = useState(readAgentDrawerWidth);
  const compactAssistant = useCompactLayout();
  const headerCompact = useHeaderCompact();
  const isEntryRoute = location.pathname === "/";
  const showAppHeader = isAuthResolved && isAuthenticated && !isEntryRoute;
  useCompactChrome(compactChromeActive(showAppHeader, compactAssistant, location.pathname));
  useVisualViewportHeight(compactAssistant);
  // AI Assist pins the shell to the screen. The transcript is the only
  // scroller; the app header, banners, and composer stay on the page.
  // The floating launcher stays hidden here: it would only open a second,
  // smaller copy of the assistant already on screen.
  const aiAssistPage = location.pathname.startsWith("/ai-assist");
  const lockShellScroll = compactAssistant || aiAssistPage;
  const showAgentLauncher = showAppHeader && !aiAssistPage;

  // When the launcher is hidden (e.g. on /ai-assist), drop any open state so
  // navigating back does not remount the launcher already open, hiding the FAB.
  useEffect(() => {
    if (!showAgentLauncher) {
      // After the route paints, so the launcher is not closed on the same frame it becomes hidden.
      // oxlint-disable-next-line react/set-state-in-effect
      setAgentOpen(false);
    }
  }, [showAgentLauncher]);

  const onAgentWidthChange = (width: number) => {
    setAgentWidth(width);
    writeAgentDrawerWidth(width);
  };

  // Below md the assistant is a temporary fullscreen overlay, so the shell and
  // other right-anchored drawers must not reserve a docked strip.
  const agentDocked = showAgentLauncher && agentOpen && !compactAssistant;
  const showAgentSession = showAppHeader && agentConfig.enabled;

  const shell = (
    <>
      {/* The docked assistant aside pushes the whole app left instead of
          overlaying it, so shift the shell by the drawer width while open. */}
      <Box
        sx={(theme) => ({
          ...shellHeightSx(compactAssistant, aiAssistPage),
          overflow: lockShellScroll ? "hidden" : undefined,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          marginRight: agentDocked ? `${agentWidth}px` : 0,
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
          paddingBottom:
            showAppHeader && headerCompact
              ? `calc(${chromeRemaining(MOBILE_BOTTOM_NAV_HEIGHT_PX)} + env(safe-area-inset-bottom, 0px))`
              : 0,
          transition: compactAssistant ? "none" : shellMarginTransition(theme, agentDocked),
        })}
      >
        {showAppHeader && <AppHeader />}
        {showAppHeader && <DemoModeBanner />}
        {showAppHeader && <QuotaBanner />}
        <Box
          component="main"
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: lockShellScroll ? "hidden" : "visible",
          }}
        >
          <Outlet />
        </Box>
      </Box>
      {showAgentLauncher && (
        <AgentDrawerLauncher
          open={agentOpen}
          onOpenChange={setAgentOpen}
          width={agentWidth}
          onWidthChange={onAgentWidthChange}
        />
      )}
    </>
  );

  return (
    // Share the assistant open flag so right-anchored overlays (e.g. the bond
    // detail drawer) can slot themselves left of the aside instead of under it.
    <DockedAsideProvider open={agentDocked} width={agentWidth}>
      {showAgentSession ? (
        <AgentSessionProvider drawerOpen={agentOpen} onOpenDrawer={() => setAgentOpen(true)}>
          {shell}
        </AgentSessionProvider>
      ) : (
        shell
      )}
    </DockedAsideProvider>
  );
}

function SessionCheckingView() {
  return <RouteFallback label="Checking your session" />;
}

export function RequireAuthenticated({ children }: Readonly<PropsWithChildren>) {
  const { isAuthenticated, isAuthResolved } = useAuth();

  if (!isAuthResolved) {
    return <SessionCheckingView />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export function HomePageEntry() {
  // useAuth returns a stable, bound signIn; `this` is never rebound.
  // oxlint-disable-next-line typescript/unbound-method
  const { isAuthenticated, isAuthResolved, error, signIn } = useAuth();

  // A browser that has signed in before is a returning user whose ~24h Entra
  // refresh token has likely expired. Auto-redirect them to the IdP: a silent
  // bounce while the Entra session cookie is alive, or the Microsoft sign-in
  // page if it isn't — either way, no manual "Sign in" click. First-time
  // visitors (no flag) never redirect and keep seeing the landing page below.
  // Read once on mount so clearing the flag mid-render can't flip the branch.
  // A revoked account lands here with a live Entra session, so it must never
  // auto-redirect: Entra would sign it straight back in and the API refuse it again.
  const location = useLocation();
  const [accessDenied] = useState(() => isAccessDeniedEntry(location.searchStr));
  const [returning] = useState(() => !accessDenied && isReturningUser());

  useEffect(() => {
    if (error) {
      console.error("[auth] sign-in failed", error);
    }
  }, [error]);

  useEffect(() => {
    if (isAuthResolved && !isAuthenticated && !error && returning) {
      void signIn();
    }
  }, [isAuthResolved, isAuthenticated, error, returning, signIn]);

  if (!isAuthResolved) {
    return <SessionCheckingView />;
  }

  if (isAuthenticated) {
    return <Navigate to="/markets" replace />;
  }

  // Returning user with no sign-in error: the effect above is redirecting to
  // the IdP, so show the checking state instead of flashing the landing page.
  if (returning && !error) {
    return <SessionCheckingView />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box
          component="img"
          src="/assets/logo.png"
          alt="Tower"
          width={64}
          height={64}
          sx={{ objectFit: "contain" }}
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          TowerBonds
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center" }}>
          Fixed Income Data Intelligence
        </Typography>
        {accessDenied && (
          <Alert severity="warning" sx={{ width: "100%" }}>
            This account doesn&apos;t have access to Tower. Contact your firm administrator.
          </Alert>
        )}
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          onClick={() => {
            void signIn();
          }}
          sx={{ mt: 2 }}
        >
          Sign in
        </Button>
        {error && (
          <Alert severity="error" sx={{ width: "100%" }}>
            Sign-in failed. Please try again.
          </Alert>
        )}
        <Link
          href="https://www.ctsbonds.com"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ mt: 1, textAlign: "center" }}
        >
          Join the waitlist
        </Link>
      </Box>
    </Box>
  );
}

export function InvitePageEntry() {
  // useAuth returns a stable, bound signIn; `this` is never rebound.
  // oxlint-disable-next-line typescript/unbound-method
  const { isAuthenticated, isAuthResolved, signIn } = useAuth();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.searchStr), [location.searchStr]);
  const domainHint = searchParams.get("domainHint")?.trim() ?? "";
  const email = searchParams.get("email")?.trim() ?? "";

  useEffect(() => {
    if (!isAuthResolved || isAuthenticated) {
      return;
    }

    void signIn({
      domainHint,
      loginHint: email,
      prompt: "select_account",
    });
  }, [domainHint, email, isAuthenticated, isAuthResolved, signIn]);

  if (!isAuthResolved) {
    return <SessionCheckingView />;
  }

  if (isAuthenticated) {
    return <Navigate to="/markets" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 460,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src="/assets/logo.png"
          alt="Tower"
          width={64}
          height={64}
          sx={{ objectFit: "contain" }}
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Redirecting to Microsoft
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Continue with the invited email address
          {email ? ` ${email}` : ""}. Microsoft will route you to your organization&apos;s sign-in
          or your Tower email/password account.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          If your account was created locally, use the temporary password from your invite email.
        </Typography>
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={() => {
            void signIn({
              domainHint,
              loginHint: email,
              prompt: "select_account",
            });
          }}
          sx={footerPrimaryButtonSx}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
}

export function HealthPage() {
  return (
    <main>
      <h1>Health</h1>
      <p>API base URL: {apiConfig.baseUrl}</p>
    </main>
  );
}
